import { getAllConnectedBrands } from '@/services/multiBrand.service';
import { MULTIBRAND_TOKENS_KEY } from '@/utils/multibrand-utils';

import { getDiscountTemplates } from '../../services/affiliate.service';
import { getCommissionTiers } from '../../services/commissionTier.service';
import { listAllEmails } from '../../services/emailAccounts.service';
import { getInboundEmailTemplates } from '../../services/inboundEmailTemplate.service';
import { getUserInfo } from '../../services/list.service';
import { getTeamMembers } from '../../services/multiUserAccess.service';
import { getAllTagsByUserId } from '../../services/tags.service';
import { getUserProfile } from '../../services/user.service';
import { getUserSettings } from '../../services/userSettings.service';
import { checkFeatureFlag, removeStorage, setStorage } from '../../utils/auth-utils';
import {
  CLOSE_CONDITIONAL_RATNA_WON_MODAL,
  HIDE_BUY_RATNA_MODAL,
  SET_BETA_USER_STATUS,
  SET_BUY_RATNA_MODAL_LEFT_SECTION,
  SET_COMMISSION_TIER_DATA,
  SET_CONNECTED_EMAILS_DATA,
  SET_DISCOUNT_TEMPLATES_DATA,
  SET_INBOUND_EMAIL_TEMPLATES_DATA,
  SET_MULTI_BRAND_ACCOUNTS,
  SET_MULTI_USER_ACCESS,
  SET_SUBSCRIPTION_STATUS,
  SET_TAGS_DATA,
  SET_TEAM_MEMBER_ID,
  SET_USER_DATA,
  SET_USER_PROFILE_DATA,
  SET_USER_SETTINGS_DATA,
  SHOW_BUY_RATNA_MODAL,
  SHOW_CONDITIONAL_RATNA_WON_MODAL,
  UPDATE_DOWNTIME_BANNER,
  UPDATE_PARTNERSHIP_LIMIT_REACHED,
  UPDATE_USER_INBOUND_EMAIL_TEMPLATES_OPTIMISTICALLY,
  USER_LOGOUT,
} from './userTypes';

export const checkBetaUserStatus = () => async (dispatch) => {
  const res = await checkFeatureFlag();
  dispatch({
    type: SET_BETA_USER_STATUS,
    payload: res,
  });
};

export const showBuyRatnaModal = (mode) => {
  return {
    type: SHOW_BUY_RATNA_MODAL,
    payload: mode,
  };
};

export const setShowBuyRatnaModalLeftSection = (section) => {
  return {
    type: SET_BUY_RATNA_MODAL_LEFT_SECTION,
    payload: section,
  };
};

export const hideBuyRatnaModal = () => {
  return {
    type: HIDE_BUY_RATNA_MODAL,
  };
};

export const showConditionalRatnaWonModal = (status, message, redirectTo) => {
  return {
    type: SHOW_CONDITIONAL_RATNA_WON_MODAL,
    payload: { status, message, redirectTo },
  };
};

export const closeConditionalRatnaWonModal = () => {
  return {
    type: CLOSE_CONDITIONAL_RATNA_WON_MODAL,
  };
};

export const setUserDetails = (userData) => {
  return {
    type: SET_USER_DATA,
    payload: userData,
  };
};

export const fetchUserSetting = () => async (dispatch) => {
  const response = await getUserSettings();
  if (response?.status) {
    dispatch({
      type: SET_USER_SETTINGS_DATA,
      payload: response.data,
    });
  }
};

export const updateUserSetting = (updatedSetting) => {
  return {
    type: SET_USER_SETTINGS_DATA,
    payload: updatedSetting,
  };
};

export const fetchUserInfo = () => async (dispatch) => {
  const response = await getUserInfo();
  if (response?.status) {
    dispatch({
      type: SET_USER_DATA,
      payload: response.data,
    });
  }
  if (response?.data?.totalActivePartnerships && response?.data?.featureLimit?.activePartnerships) {
    if (response?.data?.totalActivePartnerships >= response?.data?.featureLimit?.activePartnerships) {
      dispatch({
        type: UPDATE_PARTNERSHIP_LIMIT_REACHED,
        payload: true,
      });
    } else {
      dispatch({
        type: UPDATE_PARTNERSHIP_LIMIT_REACHED,
        payload: false,
      });
    }
  }
};

export const setTeamMemberId = (teamMemberId) => {
  return {
    type: SET_TEAM_MEMBER_ID,
    payload: teamMemberId,
  };
};

export const fetchUserProfileData = () => async (dispatch) => {
  const response = await getUserProfile();
  if (response?.status) {
    dispatch({
      type: SET_USER_PROFILE_DATA,
      payload: response.data,
    });
  }
};

export const fetchDiscountTemplates = () => async (dispatch) => {
  const response = await getDiscountTemplates();

  if (response?.status) {
    dispatch({
      type: SET_DISCOUNT_TEMPLATES_DATA,
      payload: response.data,
    });
  }
};

export const fetchTeamMembers = () => async (dispatch) => {
  const response = await getTeamMembers();

  if (response?.status) {
    dispatch({
      type: SET_MULTI_USER_ACCESS,
      payload: response.data,
    });
  }
};

export const fetchInboundEmailTemplates = () => async (dispatch) => {
  const response = await getInboundEmailTemplates();
  if (response?.status) {
    dispatch({
      type: SET_INBOUND_EMAIL_TEMPLATES_DATA,
      payload: response.data,
    });
  }
};

export const updateUserInboundEmailTemplatesOptimistically = (templates) => {
  return {
    type: UPDATE_USER_INBOUND_EMAIL_TEMPLATES_OPTIMISTICALLY,
    payload: templates,
  };
};

export const fetchConnectedEmails = () => async (dispatch) => {
  const response = await listAllEmails();
  if (response?.status) {
    dispatch({
      type: SET_CONNECTED_EMAILS_DATA,
      payload: response.data,
    });
  } else {
    dispatch({
      type: SET_CONNECTED_EMAILS_DATA,
      payload: [],
    });
  }
};

export const fetchTags = () => async (dispatch) => {
  const response = await getAllTagsByUserId();
  if (response?.status) {
    dispatch({
      type: SET_TAGS_DATA,
      payload: response.data,
    });
  }
};

export const fetchCommissionTiers = () => async (dispatch) => {
  const response = await getCommissionTiers();
  if (response?.status) {
    dispatch({
      type: SET_COMMISSION_TIER_DATA,
      payload: response.data,
    });
  }
};

export const fetchMultiBrandAccounts = (tokens) => async (dispatch) => {
  const response = await getAllConnectedBrands(tokens);
  if (response?.status) {
    const updateTokens = response?.data?.map((brand) => {
      return {
        token: brand?.refreshedToken,
        teamMemberId: brand?.connectedBrandTeamMemberId,
        userId: brand?.connectedBrandUserId,
      };
    });

    setStorage(MULTIBRAND_TOKENS_KEY, JSON.stringify(updateTokens));

    dispatch({
      type: SET_MULTI_BRAND_ACCOUNTS,
      payload: response.data,
    });
  } else {
    removeStorage(MULTIBRAND_TOKENS_KEY);
    fetchMultiBrandAccounts([]);
  }
};

export const setSubscriptionStatus = (status) => async (dispatch) => {
  dispatch({
    type: SET_SUBSCRIPTION_STATUS,
    payload: status,
  });
};

export const logoutUser = () => {
  return {
    type: USER_LOGOUT,
  };
};

export const updateDowntimeBanner = (isDismissed) => (dispatch) => {
  dispatch({
    type: UPDATE_DOWNTIME_BANNER,
    payload: isDismissed,
  });
};

export const updatePartnershipLimitReached = (limitReached) => (dispatch) => {
  dispatch({
    type: UPDATE_PARTNERSHIP_LIMIT_REACHED,
    payload: limitReached,
  });
};
